/**
 * SmartEwmLayout — the standalone SmartEWM application shell.
 *
 * Visually distinct from the WarehouseX / Consultant workspace:
 *  - Lighter PrimeS4 blue-accent sidebar (no `bg-[#111827]` consultant chrome).
 *  - Only SmartEWM modules are exposed in the navigation.
 *  - Authenticated users with a non-SmartEWM `application_access` are
 *    redirected back to the consultant workspace so the two shells stay
 *    cleanly separated.
 *
 * Sidebar (per Feb 2026 product brief):
 *   1. Ask PRIVA           (active)
 *   2. Support Assistant   (disabled — coming soon)
 *   3. SmartCenter         (disabled — coming soon)
 *   4. Settings            (disabled — coming soon)
 */
import { useEffect, useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { adminLogout, adminMe } from "../lib/adminApi";
import usePlatformLogo from "../lib/usePlatformLogo";
import { hasSmartEwm } from "../lib/applications";
import { Sparkles, LogOut, UserCircle } from "lucide-react";
import ProfileDialog from "../components/ProfileDialog";

const NAV_ITEMS = [
  {
    to: "/app/smartewmbot",
    label: "Agent Workspace",
    icon: Sparkles,
    enabled: true,
  },
];

export default function SmartEwmLayout() {
  const [user, setUser] = useState(null);
  const [checking, setChecking] = useState(true);
  const [profileOpen, setProfileOpen] = useState(false);
  const navigate = useNavigate();
  const platformLogo = usePlatformLogo();

  useEffect(() => {
    adminMe()
      .then((u) => {
        setUser(u);
        setChecking(false);
        // Access-aware segregation: only users who can access SmartEWM (either
        // directly or via the WarehouseX Transformation umbrella) may enter
        // this shell. Everyone else is bounced to the consultant workspace.
        if (!hasSmartEwm(u)) {
          navigate("/consultant/assessments", { replace: true });
        }
      })
      .catch(() => {
        setChecking(false);
        navigate("/transformation", { replace: true });
      });
  }, [navigate]);

  const handleLogout = async () => {
    await adminLogout();
    navigate("/transformation", { replace: true });
  };

  if (checking) {
    return (
      <div className="min-h-screen bg-[#F8FAFC] flex items-center justify-center">
        <p className="text-sm text-[#6B7280]">Loading…</p>
      </div>
    );
  }
  if (!user) return null;

  return (
    <div className="h-screen bg-[#F8FAFC] flex overflow-hidden" data-testid="smartewm-shell">
      <aside
        className="sticky top-0 self-start h-screen w-60 bg-white border-r border-[#E2E8F0] flex flex-col shrink-0"
        data-testid="smartewm-sidebar"
      >
        {/* Brand */}
        <div className="px-4 py-4 border-b border-[#E2E8F0]">
          <div className="flex items-center gap-2" data-testid="smartewm-brand">
            {platformLogo ? (
              <img loading="lazy"
                src={platformLogo}
                alt="PrimeS4 AI"
                className="h-7 w-auto object-contain"
              />
            ) : (
              <p
                className="text-sm font-bold text-[#0F172A]"
                style={{ fontFamily: "Chivo, sans-serif" }}
              >
                PrimeS4 AI
              </p>
            )}
          </div>
          <p
            className="text-[10px] uppercase tracking-wider text-[#94A3B8] mt-2 font-semibold"
            data-testid="smartewm-product-tag"
          >
            SmartEWM Workspace
          </p>
          <p
            className="text-[10px] text-[#64748B] mt-1.5 truncate"
            data-testid="smartewm-user-email"
          >
            {user.email}
          </p>
        </div>

        {/* Nav */}
        <nav className="flex-1 py-3" data-testid="smartewm-nav">
          {NAV_ITEMS.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end
              className={({ isActive }) =>
                `flex items-center gap-2.5 px-4 py-2 text-xs font-medium transition-colors ${
                  isActive
                    ? "bg-[#E0F2FE] text-[#025979] border-l-2 border-[#025979]"
                    : "text-[#475569] hover:bg-[#F1F5F9]"
                }`
              }
              data-testid={`smartewm-nav-${item.label
                .toLowerCase()
                .replace(/\s+/g, "-")}`}
            >
              <item.icon className="w-3.5 h-3.5" strokeWidth={1.5} />
              {item.label}
            </NavLink>
          ))}
        </nav>

        {/* Footer */}
        <div className="p-3 border-t border-[#E2E8F0] space-y-2">
          <p
            data-testid="smartewm-powered-by"
            className="text-[10px] leading-snug text-[#94A3B8] select-none"
          >
            Powered by PRIVA Operational Intelligence
          </p>
          <button
            type="button"
            onClick={() => setProfileOpen(true)}
            className="flex items-center gap-2 text-xs text-[#475569] hover:text-[#0F172A] transition-colors"
            data-testid="smartewm-my-profile-btn"
          >
            <UserCircle className="w-3.5 h-3.5" /> My Profile
          </button>
          <button
            type="button"
            onClick={handleLogout}
            className="flex items-center gap-2 text-xs text-[#475569] hover:text-[#0F172A] transition-colors"
            data-testid="smartewm-logout-btn"
          >
            <LogOut className="w-3.5 h-3.5" /> Logout
          </button>
        </div>
      </aside>

      <main className="flex-1 min-w-0 h-full overflow-hidden flex flex-col p-4 sm:p-6 lg:p-8">
        <Outlet context={{ user }} />
      </main>
      <ProfileDialog open={profileOpen} onOpenChange={setProfileOpen} user={user} onProfileUpdated={setUser} />
    </div>
  );
}
